import React, { useEffect } from 'react';
import { Grid, Typography, IconButton, Snackbar, Alert, MenuItem, RadioGroup, FormControlLabel, Radio, Stack, Icon, Button, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DeleteModal from './DeleteModal';
import { InputField } from '../../../components/InputController';
import CustomDropdown from '../../../components/customDropdown';
import DeleteIcon from '../../../icons/deleteIcon.svg';
import ChildQuestion from '../../../icons/ChildQuestion.svg';
import AddIcon from '../../../icons/addcard.png';
import { ApplicationQuestionDto, CustomQuestionTypeEnum, CustomQuestionResponseTypeEnum } from '@inosio/payments-sdk';
import { clearPropertyGroupConfigSettingsFlags, getAllPropertyGroupsConfigs, getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

interface ErrorObj {
  [key: string]: string;
}

interface PropsType {
  CustomQuestionType: CustomQuestionTypeEnum;
}

interface ExtendedApplicationQuestionDto extends ApplicationQuestionDto {
  error: ErrorObj;
  showNewForm?: boolean;
  newForm?: ExtendedApplicationQuestionDto;
  isUpdating?: boolean;
}

export default function ApplicationsPersonal(props: PropsType) {
  const dispatch = useDispatch();

  const [snackBarOpen, setSnackBarOpen] = React.useState(false);
  const [snakBarError, setSnakBarError] = React.useState('');
  const { selectedPropertyGroupAssignedConfigs, UpdatingPropertyGroupConfigsError, isPropertyGroupAssignedConfigsRefreshRequired, isUpdatingPropertyGroupConfigsSuccess } = useSelector(selectPropertyGroupDetailsState);
  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
  };
  useEffect(() => {
    if (UpdatingPropertyGroupConfigsError.message?.length > 0) {
      setSnackBarOpen(true);
      setSnakBarError(UpdatingPropertyGroupConfigsError.message);
    } else {
      setSnakBarError('');
    }
  }, [UpdatingPropertyGroupConfigsError]);

  const getTypography = (text: string, fontWeight: number, fontSize: string, restProps?: object) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
        ...restProps,
      }}
    >
      {text}
    </Typography>
  );

  const getTypographyLabel = (text: string, restProps?: object) => (
    <Typography
      sx={{
        fontFamily: 'Open Sans',
        fontWeight: '700',
        fontSize: '18px',
        lineHeight: '25px',
        color: '#14142B',
        ...restProps,
      }}
    >
      {text}
    </Typography>
  );

  const [newQuestion, setNewQuestion] = React.useState<ExtendedApplicationQuestionDto>({ error: {} });
  const handleSave = () => {
    setNewQuestion({ ...newQuestion, error: {} });
    if (!newQuestion.QuestionText) {
      setNewQuestion({ ...newQuestion, error: { QuestionText: 'Required field' } });
      return;
    }
    if (!newQuestion.CustomQuestionResponseType) {
      setNewQuestion({ ...newQuestion, error: { CustomQuestionResponseType: 'Required field' } });
      return;
    }

    setNewQuestion({ ...newQuestion, isUpdating: true });

    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
        productTypeId: 2,
        updatedPmConfig: {
          ApplicationsQuestions: [
            {
              QuestionText: newQuestion.QuestionText,
              CustomQuestionType: props.CustomQuestionType,
              CustomQuestionResponseType: newQuestion.CustomQuestionResponseType,
              IsRequired: newQuestion.IsRequired,
              // ParentCustomQuestionUuid: "2e200043-02de-44a4-b485-0405068f0e1c",
            },
          ],
        },
      }),
    );
  };

  useEffect(() => {
    if (isUpdatingPropertyGroupConfigsSuccess) {
      dispatch(clearPropertyGroupConfigSettingsFlags({}));
      if (isPropertyGroupAssignedConfigsRefreshRequired) dispatch(getAllPropertyGroupsConfigs());
      setNewQuestion({ error: {} });
      setRemovalUuid('');
    }
  }, [isUpdatingPropertyGroupConfigsSuccess]);

  const [questions, setQuestions] = React.useState<ExtendedApplicationQuestionDto[]>([]);

  useEffect(() => {
    const questions = selectedPropertyGroupAssignedConfigs.ApplicationsQuestions ?? [];
    const extendedQuestions = questions
      .filter((q) => q.CustomQuestionType === props.CustomQuestionType)
      .map((q) => {
        return {
          ...q,
          error: {},
          showNewForm: false,
          newForm: !q.ParentCustomQuestionUuid ? { error: {} } : undefined,
        } as ExtendedApplicationQuestionDto;
      });

    setQuestions(extendedQuestions);
  }, [selectedPropertyGroupAssignedConfigs]);

  const handleUpdate = (uuid: string) => {
    let target = questions.find((q) => q.Uuid === uuid);

    if (target && !target?.QuestionText) {
      setQuestions((questions) =>
        questions.map((q) => {
          if (q.Uuid === uuid) return { ...q, error: { QuestionText: 'Required field' } };
          return q;
        }),
      );
      return;
    }

    if (target && !target?.CustomQuestionResponseType) {
      setQuestions((questions) =>
        questions.map((q) => {
          if (q.Uuid === uuid) return { ...q, error: { CustomQuestionResponseType: 'Required field' } };
          return q;
        }),
      );
      return;
    }

    setQuestions((questions) =>
      questions.map((q) => {
        if (q.Uuid === uuid) return { ...q, isUpdating: true };
        return q;
      }),
    );

    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
        productTypeId: 2,
        updatedPmConfig: {
          ApplicationsQuestions: [
            {
              Uuid: target?.Uuid,
              QuestionText: target?.QuestionText,
              CustomQuestionType: props.CustomQuestionType,
              CustomQuestionResponseType: target?.CustomQuestionResponseType,
              IsRequired: target?.IsRequired,
              ParentCustomQuestionUuid: target?.ParentCustomQuestionUuid,
            },
          ],
        },
      }),
    );
  };

  const [removalUuid, setRemovalUuid] = React.useState('');
  const handleDelete = (uuid: string) => {
    setRemovalUuid(uuid);
    setIsDeleteModalOpen(true);
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const handleConfirmDeleteModal = () => {
    setIsDeleteModalOpen(false);
    if (removalUuid) {
      dispatch(
        getUpdatePropertyGroupConfigs({
          propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
          productTypeId: 2,
          updatedPmConfig: {
            ApplicationsQuestions: [
              {
                Uuid: removalUuid,
                IsDeleting: true,
              },
            ],
          },
        }),
      );
    }
  };

  const handleSaveChildQuestion = (parentUuid: string) => {
    const parent = questions.find((q) => q.Uuid === parentUuid);

    if (!parent?.newForm?.QuestionText) {
      setQuestions(
        questions.map((q) => {
          if (q.Uuid === parentUuid) {
            return { ...q, newForm: { ...(parent?.newForm ?? {}), error: { QuestionText: 'Required field' } } };
          }
          return q;
        }),
      );
      return;
    }

    if (!parent?.newForm?.CustomQuestionResponseType) {
      setQuestions(
        questions.map((q) => {
          if (q.Uuid === parentUuid) {
            return { ...q, newForm: { ...(parent?.newForm ?? {}), error: { CustomQuestionResponseType: 'Required field' } } };
          }
          return q;
        }),
      );
      return;
    }

    setQuestions(
      questions.map((q) => {
        if (q.Uuid === parentUuid) {
          return { ...q, newForm: { ...(parent?.newForm ?? { error: {} }), isUpdating: true } };
        }
        return q;
      }),
    );

    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
        productTypeId: 2,
        updatedPmConfig: {
          ApplicationsQuestions: [
            {
              QuestionText: parent?.newForm.QuestionText,
              CustomQuestionType: props.CustomQuestionType,
              CustomQuestionResponseType: parent?.newForm.CustomQuestionResponseType,
              IsRequired: parent?.newForm.IsRequired,
              ParentCustomQuestionUuid: parentUuid,
            },
          ],
        },
      }),
    );
  };

  return (
    <Grid item xs={12} md={12}>
      <div style={{ border: '1px solid #EEEEEE' }}></div>
      {getTypographyLabel('Custom Fields:', { m: 2 })}
      <div style={{ border: '1px solid #EEEEEE' }}></div>

      <Grid container sx={{ my: 2.5, mx: 3.5 }}>
        <Grid item xs={12} md={6} lg={5}>
          {getTypographyLabel('Question')}
        </Grid>
        <Grid item xs={6} md={3} lg={2} sx={{ display: { xs: 'none', md: 'block' } }}>
          {getTypographyLabel('Type', { ml: 2 })}
        </Grid>
        <Grid item xs={6} md={3} lg={2} sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
          {getTypographyLabel('Required')}
        </Grid>
        <Grid item xs={6} md={8} lg={1} sx={{ display: { xs: 'none', lg: 'block' } }}>
          {getTypographyLabel('Delete')}
        </Grid>
        <Grid item xs={6} md={4} lg={2}></Grid>
      </Grid>

      {questions
        .filter((q) => !q.ParentCustomQuestionUuid)
        .map((question) => (
          <div key={question.Uuid}>
            <Grid container sx={{ mx: 3.5, mt: 1, mb: -1 }}>
              <Grid item xs={12} md={6} lg={5}>
                <Stack sx={{ pr: { sm: 4, xs: 2 } }}>
                  <InputField
                    name={'QuestionText'}
                    label={'Question or Data Request'}
                    value={question.QuestionText ?? ''}
                    isError={false}
                    errors={question.error['QuestionText']}
                    touched={!!question.error['QuestionText']}
                    onChange={(e) => {
                      setQuestions(
                        questions.map((q) => {
                          if (q.Uuid === question.Uuid) {
                            return { ...q, QuestionText: e.target.value };
                          }
                          return q;
                        }),
                      );
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={6} md={3} lg={2}>
                <Stack paddingRight={2}>
                  <CustomDropdown
                    isFullWidth
                    label={'Type'}
                    value={question.CustomQuestionResponseType ?? ''}
                    handleChange={(e) => {
                      setQuestions(
                        questions.map((q) => {
                          if (q.Uuid === question.Uuid) {
                            return { ...q, CustomQuestionResponseType: e.target.value as CustomQuestionResponseTypeEnum };
                          }
                          return q;
                        }),
                      );
                    }}
                    menuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '24px',
                        },
                      },
                    }}
                    error={question.error['CustomQuestionResponseType']}
                  >
                    <MenuItem style={{ height: '36px' }} disabled value={''}>
                      Select Type
                    </MenuItem>
                    {Object.values(CustomQuestionResponseTypeEnum).map((type, index) => (
                      <MenuItem style={{ height: '36px' }} value={type} key={index}>
                        {type === 'Boolean' ? 'Yes/No' : type.split(/(?=[A-Z])/).join(' ')}
                      </MenuItem>
                    ))}
                  </CustomDropdown>
                </Stack>
              </Grid>
              <Grid item xs={6} md={3} lg={2}>
                <Stack direction="row" justifyContent="center" alignItems="center" height={49}>
                  <RadioGroup
                    value={question.IsRequired}
                    row
                    onChange={(e) => {
                      setQuestions(
                        questions.map((q) => {
                          if (q.Uuid === question.Uuid) {
                            return { ...q, IsRequired: e.target.value === 'true' };
                          }
                          return q;
                        }),
                      );
                    }}
                  >
                    <FormControlLabel value={true} control={<Radio size="small" />} label="Yes" />
                    <FormControlLabel value={false} control={<Radio size="small" />} label="No" />
                  </RadioGroup>
                </Stack>
              </Grid>
              <Grid item xs={6} md={8} lg={1}>
                <Stack direction="row" alignItems="center" height={49} paddingLeft={2} sx={{ justifyContent: { xs: 'end', lg: 'flex-start' } }}>
                  <IconButton onClick={() => handleDelete(question.Uuid ?? '')} sx={{ p: 0.25 }}>
                    <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img
                        style={{
                          cursor: 'pointer',
                          width: '15px',
                          height: '18px',
                        }}
                        src={DeleteIcon}
                        alt="Delete"
                      />
                    </Icon>
                  </IconButton>
                </Stack>
              </Grid>
              <Grid item xs={6} md={4} lg={2}>
                <Stack direction="row" alignItems="center" height={49} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                  <Button
                    variant="contained"
                    style={{
                      textTransform: 'none',
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      backgroundColor: '#39A2DB',
                      borderRadius: '24px',
                      width: '115px',
                      height: '36.5px',
                    }}
                    onClick={() => handleUpdate(question.Uuid ?? '')}
                  >
                    {question.isUpdating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Update'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {questions
              .filter((cq) => cq.ParentCustomQuestionUuid === question.Uuid)
              .map((childQuestion) => (
                <Grid container sx={{ mx: 3.5, mt: 1, mb: -1 }} key={childQuestion.Uuid}>
                  <Grid item xs={12} md={6} lg={5}>
                    <Stack direction="row" justifyContent="space-between" sx={{ pr: { sm: 4, xs: 2 } }}>
                      <img
                        style={{
                          width: '30px',
                          height: '39px',
                          marginLeft: '33px',
                          marginRight: '14px',
                        }}
                        src={ChildQuestion}
                        alt="Child"
                      />
                      <InputField
                        name={'QuestionText'}
                        label={'Question or Data Request'}
                        value={childQuestion.QuestionText ?? ''}
                        isError={false}
                        errors={childQuestion.error['QuestionText']}
                        touched={!!childQuestion.error['QuestionText']}
                        onChange={(e) => {
                          setQuestions(
                            questions.map((q) => {
                              if (q.Uuid === childQuestion.Uuid) {
                                return { ...q, QuestionText: e.target.value };
                              }
                              return q;
                            }),
                          );
                        }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={6} md={3} lg={2}>
                    <Stack paddingRight={2}>
                      <CustomDropdown
                        isFullWidth
                        label={'Type'}
                        value={childQuestion.CustomQuestionResponseType ?? ''}
                        handleChange={(e) => {
                          setQuestions(
                            questions.map((q) => {
                              if (q.Uuid === childQuestion.Uuid) {
                                return { ...q, CustomQuestionResponseType: e.target.value as CustomQuestionResponseTypeEnum };
                              }
                              return q;
                            }),
                          );
                        }}
                        menuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: '24px',
                            },
                          },
                        }}
                        error={childQuestion.error['CustomQuestionResponseType']}
                      >
                        <MenuItem style={{ height: '36px' }} disabled value={''}>
                          Select Type
                        </MenuItem>
                        {Object.values(CustomQuestionResponseTypeEnum).map((type, index) => (
                          <MenuItem style={{ height: '36px' }} value={type} key={index}>
                            {type === 'Boolean' ? 'Yes/No' : type.split(/(?=[A-Z])/).join(' ')}
                          </MenuItem>
                        ))}
                      </CustomDropdown>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} md={3} lg={2}>
                    <Stack direction="row" justifyContent="center" alignItems="center" height={49}>
                      <RadioGroup
                        value={childQuestion.IsRequired}
                        row
                        onChange={(e) => {
                          setQuestions(
                            questions.map((q) => {
                              if (q.Uuid === childQuestion.Uuid) {
                                return { ...q, IsRequired: e.target.value === 'true' };
                              }
                              return q;
                            }),
                          );
                        }}
                      >
                        <FormControlLabel value={true} control={<Radio size="small" />} label="Yes" />
                        <FormControlLabel value={false} control={<Radio size="small" />} label="No" />
                      </RadioGroup>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} md={8} lg={1}>
                    <Stack direction="row" alignItems="center" height={49} paddingLeft={2} sx={{ justifyContent: { xs: 'end', lg: 'flex-start' } }}>
                      <IconButton onClick={() => handleDelete(childQuestion.Uuid ?? '')} sx={{ p: 0.25 }}>
                        <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img
                            style={{
                              cursor: 'pointer',
                              width: '15px',
                              height: '18px',
                            }}
                            src={DeleteIcon}
                            alt="Delete"
                          />
                        </Icon>
                      </IconButton>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} md={4} lg={2}>
                    <Stack direction="row" alignItems="center" height={49} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                      <Button
                        variant="contained"
                        style={{
                          textTransform: 'none',
                          fontFamily: "'Open Sans', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 700,
                          backgroundColor: '#39A2DB',
                          borderRadius: '24px',
                          width: '115px',
                          height: '36.5px',
                        }}
                        onClick={() => handleUpdate(childQuestion.Uuid ?? '')}
                      >
                        {childQuestion.isUpdating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Update'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              ))}

            {question.showNewForm ? (
              <Grid container sx={{ mx: 3.5, mt: 1, mb: 2 }}>
                <Grid item xs={12} md={6} lg={5}>
                  <Stack direction="row" justifyContent="space-between" sx={{ pr: { sm: 4, xs: 2 } }}>
                    <img
                      style={{
                        width: '30px',
                        height: '39px',
                        marginLeft: '33px',
                        marginRight: '14px',
                      }}
                      src={ChildQuestion}
                      alt="Child"
                    />
                    <InputField
                      name={'QuestionText'}
                      label={'Question or Data Request'}
                      value={question.newForm?.QuestionText ?? ''}
                      isError={false}
                      errors={question.newForm?.error['QuestionText']}
                      touched={!!question.newForm?.error['QuestionText']}
                      onChange={(e) => {
                        setQuestions(
                          questions.map((q) => {
                            if (q.Uuid === question.Uuid) {
                              return { ...q, newForm: { ...(question.newForm ?? { error: {} }), QuestionText: e.target.value } };
                            }
                            return q;
                          }),
                        );
                      }}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6} md={3} lg={2}>
                  <Stack paddingRight={2}>
                    <CustomDropdown
                      isFullWidth
                      label={'Type'}
                      value={question.newForm?.CustomQuestionResponseType ?? ''}
                      handleChange={(e) => {
                        setQuestions(
                          questions.map((q) => {
                            if (q.Uuid === question.Uuid) {
                              return { ...q, newForm: { ...(question.newForm ?? { error: {} }), CustomQuestionResponseType: e.target.value as CustomQuestionResponseTypeEnum } };
                            }
                            return q;
                          }),
                        );
                      }}
                      menuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: '24px',
                          },
                        },
                      }}
                      error={question.newForm?.error['CustomQuestionResponseType']}
                    >
                      <MenuItem style={{ height: '36px' }} disabled value={''}>
                        Select Type
                      </MenuItem>
                      {Object.values(CustomQuestionResponseTypeEnum).map((type, index) => (
                        <MenuItem style={{ height: '36px' }} value={type} key={index}>
                          {type === 'Boolean' ? 'Yes/No' : type.split(/(?=[A-Z])/).join(' ')}
                        </MenuItem>
                      ))}
                    </CustomDropdown>
                  </Stack>
                </Grid>
                <Grid item xs={6} md={3} lg={2}>
                  <Stack direction="row" justifyContent="center" alignItems="center" height={49}>
                    <RadioGroup
                      value={!!question.newForm?.IsRequired}
                      row
                      onChange={(e) => {
                        setQuestions(
                          questions.map((q) => {
                            if (q.Uuid === question.Uuid) {
                              return { ...q, newForm: { ...(question.newForm ?? { error: {} }), IsRequired: e.target.value === 'true' } };
                            }
                            return q;
                          }),
                        );
                      }}
                    >
                      <FormControlLabel value={true} control={<Radio size="small" />} label="Yes" />
                      <FormControlLabel value={false} control={<Radio size="small" />} label="No" />
                    </RadioGroup>
                  </Stack>
                </Grid>
                <Grid item xs={6} md={8} lg={1}>
                  <Stack direction="row" alignItems="center" height={49} paddingLeft={2} sx={{ justifyContent: { xs: 'end', lg: 'flex-start' } }}>
                    <IconButton
                      onClick={() => {
                        setQuestions(
                          questions.map((q) => {
                            if (q.Uuid === question.Uuid) {
                              return { ...q, showNewForm: false };
                            }
                            return q;
                          }),
                        );
                      }}
                      sx={{ p: 0.25 }}
                    >
                      <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                          style={{
                            cursor: 'pointer',
                            width: '15px',
                            height: '18px',
                          }}
                          src={DeleteIcon}
                          alt="Delete"
                        />
                      </Icon>
                    </IconButton>
                  </Stack>
                </Grid>
                <Grid item xs={6} md={4} lg={2}>
                  <Stack direction="row" alignItems="center" height={49} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                    <Button
                      variant="contained"
                      style={{
                        textTransform: 'none',
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 700,
                        backgroundColor: '#39A2DB',
                        borderRadius: '24px',
                        width: '115px',
                        height: '36.5px',
                      }}
                      onClick={() => handleSaveChildQuestion(question.Uuid ?? '')}
                    >
                      {question.newForm?.isUpdating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            ) : (
              <Stack direction="row" alignItems="center" paddingX={3.5} paddingBottom="15px">
                <IconButton
                  onClick={() => {
                    setQuestions(
                      questions.map((q) => {
                        if (q.Uuid === question.Uuid) {
                          return { ...q, showNewForm: true };
                        }
                        return q;
                      }),
                    );
                  }}
                  sx={{ p: 0.8, mr: '-8px' }}
                >
                  <Icon sx={{ width: 26, height: 26 }}>
                    <img
                      style={{
                        cursor: 'pointer',
                        width: 26,
                        height: 26,
                      }}
                      src={AddIcon}
                      alt="Add an item"
                    />
                  </Icon>
                </IconButton>
                {getTypography('Add sub-question', 400, '18px', { color: '#000000', ml: '17px' })}
              </Stack>
            )}
          </div>
        ))}

      <div style={{ border: '1px solid #EEEEEE' }}></div>
      <Grid container sx={{ mt: 2.5, mx: 3.5 }}>
        <Grid item xs={12} md={6} lg={5}>
          <Stack sx={{ pr: { sm: 4, xs: 2 } }}>
            <InputField name={'QuestionText'} label={'Question or Data Request'} value={newQuestion.QuestionText ?? ''} isError={false} errors={newQuestion.error['QuestionText']} touched={!!newQuestion.error['QuestionText']} onChange={(e) => setNewQuestion((question) => ({ ...question, QuestionText: e.target.value }))} />
          </Stack>
        </Grid>
        <Grid item xs={6} md={3} lg={2}>
          <Stack paddingRight={2}>
            <CustomDropdown
              isFullWidth
              label={'Type'}
              value={newQuestion.CustomQuestionResponseType ?? ''}
              handleChange={(e) => setNewQuestion((question) => ({ ...question, CustomQuestionResponseType: e.target.value as CustomQuestionResponseTypeEnum }))}
              menuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '24px',
                  },
                },
              }}
              error={newQuestion.error['CustomQuestionResponseType']}
            >
              <MenuItem style={{ height: '36px' }} disabled value={''}>
                Select Type
              </MenuItem>
              {Object.values(CustomQuestionResponseTypeEnum).map((type, index) => (
                <MenuItem style={{ height: '36px' }} value={type} key={index}>
                  {type === 'Boolean' ? 'Yes/No' : type.split(/(?=[A-Z])/).join(' ')}
                </MenuItem>
              ))}
            </CustomDropdown>
          </Stack>
        </Grid>
        <Grid item xs={6} md={3} lg={2}>
          <Stack direction="row" justifyContent="center" alignItems="center" height={49}>
            <RadioGroup value={!!newQuestion.IsRequired} row onChange={(e) => setNewQuestion((question) => ({ ...question, IsRequired: e.target.value === 'true' }))}>
              <FormControlLabel value={true} control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value={false} control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </Stack>
        </Grid>
        <Grid item xs={6} md={8} lg={1}></Grid>
        <Grid item xs={6} md={4} lg={2}>
          <Stack direction="row" alignItems="center" height={49} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
            <Button
              variant="contained"
              style={{
                textTransform: 'none',
                fontFamily: "'Open Sans', sans-serif",
                fontStyle: 'normal',
                fontWeight: 700,
                backgroundColor: '#39A2DB',
                borderRadius: '24px',
                width: '115px',
                height: '36.5px',
              }}
              onClick={handleSave}
            >
              {newQuestion.isUpdating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {snakBarError}
        </Alert>
      </Snackbar>

      {isDeleteModalOpen && <DeleteModal open={isDeleteModalOpen} confirmCallback={handleConfirmDeleteModal} closeCallback={() => setIsDeleteModalOpen(false)} />}
    </Grid>
  );
}
